import Pad from "../../classes/Pad.js";
import Positioner from "../../classes/Positioner.js";
import Observer from "../../classes/Observer.js";
import ReminderToken from "../../classes/ReminderToken.js";
import Dialog from "../../classes/Dialog.js";
import TokenStore from "../../classes/TokenStore.js";
import {
    lookup,
    lookupOne,
    lookupOneCached,
    replaceContentsMany
} from "../../utils/elements.js";

const gameObserver = Observer.create("game");
const tokenObserver = Observer.create("token");

const padElement = lookupOneCached(".js--pad");
const pad = new Pad(padElement, tokenObserver);
padElement.pad = pad;

const styleObserver = new MutationObserver((mutations) => {

    gameObserver.trigger("pad-height-change", {
        height: mutations[0].target.style.height
    });

});

styleObserver.observe(padElement, {
    attributes: true,
    attributeFilter: ["style"]
});

// If the elements are within a closed <details> element then their height and
// width will be 0. Listen for the pad becoming visible and update the class.
lookup("details").forEach((details) => {

    details.addEventListener("toggle", () => {
        pad.updateDimensions();
    });

});

gameObserver.on("characters-selected", ({ detail }) => {

    const characters = detail.characters.filter((character) => {
        const team = character.getTeam();
        return team !== "traveller" && team !== "fabled" && team !== "loric";
    });

    replaceContentsMany(
        lookupOneCached("#character-list__list"),
        characters.map((character) => character.drawList())
    );

    const reminders = characters.reduce((reminders, character) => {
        return reminders.concat(character.getReminders());
    }, ReminderToken.getGlobal());

    replaceContentsMany(
        lookupOneCached("#reminder-list__list"),
        reminders.map((reminder) => reminder.drawList())
    );

    lookupOneCached("#add-token").disabled = false;
    lookupOneCached("#add-reminder").disabled = false;
    lookupOneCached("#show-tokens").disabled = false;

});

gameObserver.on("character-drawn", ({ detail }) => {
    pad.addNewCharacter(detail.character);
});

lookupOne("#show-night-order").addEventListener("change", ({ target }) => {

    padElement.style[
        target.checked
        ? "removeProperty"
        : "setProperty"
    ]("--night-order-display", "none");

});

lookupOne("#show-first-night-order").addEventListener("change", ({ target }) => {

    padElement.style[
        target.checked
        ? "removeProperty"
        : "setProperty"
    ]("--first-night-order-display", "none");

});

lookupOne("#show-other-nights-order").addEventListener("change", ({ target }) => {

    padElement.style[
        target.checked
        ? "removeProperty"
        : "setProperty"
    ]("--other-nights-order-display", "none");

});

gameObserver.on("clear", () => pad.reset());

// Character and Reminder token sizes.

const html = document.documentElement;

lookupOne("#token-size").addEventListener("input", ({ target }) => {
    html.style.setProperty("--token-size", target.value);
});

lookupOne("#reminder-size").addEventListener("input", ({ target }) => {
    html.style.setProperty("--reminder-size", target.value);
});

// Token auto-placements.

pad.setPositioner(new Positioner());

const tokenLayout = lookupOne("#token-layout");

pad.updatePositioner({
    layout: tokenLayout.value,
    // At this stage, most of the positioner probably hasn't been set up so we'd
    // get a few issues if we try to generate the co-ordinates. Better to just
    // give the positioner some data and generate the co-ordinates later.
    generate: false
});

tokenLayout.addEventListener("change", () => {
    pad.updatePositioner({ layout: tokenLayout.value });
});

gameObserver.on("player-count", ({ detail }) => {
    pad.updatePositioner({ total: detail.count });
});

gameObserver.on("pad-height-change", () => {
    pad.updatePositioner({ container: true });
});

Dialog.create(lookupOneCached("#character-select")).on(Dialog.SHOW, () => {

    const grimoireSection = lookupOneCached("#grimoire");
    const isOpen = grimoireSection.open;

    if (!isOpen) {
        grimoireSection.open = true;
    }

    pad.updatePositioner({
        container: true,
        tokens: true,
        total: lookupOneCached("#player-count").value
    });

    if (!isOpen) {
        grimoireSection.open = false;
    }

});

TokenStore.ready((tokenStore) => {
    pad.setTokenStore(tokenStore);
});

// Layout controls: reapply / reverse / rotate. Character tokens are placed onto
// the positioner's coordinate slots (slot 0 = top, going clockwise):
//   reapply: character i -> slot i        (1 2 3 ... N)
//   reverse: character i -> slot N-1-i    (N ... 3 2 1)
//   rotate:  shift every token's slot by an offset (mod N), keeping order.
// Reapply / reverse reset the rotation; rotate-left / right adjust it by one.
let layoutReversed = false;
let layoutRotation = 0;

function applyLayout() {

    const total = pad.characters.length;

    if (total === 0) {
        return;
    }

    pad.updatePositioner({
        layout: lookupOne("#token-layout").value,
        container: true,
        tokens: true,
        total,
        generate: true
    });

    // Normalise so negative rotations (rotate-left) wrap correctly.
    const rotation = ((layoutRotation % total) + total) % total;

    pad.characters.forEach(({ token }, index) => {

        const base = layoutReversed ? (total - 1 - index) : index;
        const coords = pad.coords[(base + rotation) % total];

        if (coords && Array.isArray(coords) && coords.length >= 2) {
            const [left, top] = coords;
            pad.moveToken(token, left, top);
        }

    });

}

lookupOne("#reapply-layout").addEventListener("click", () => {
    layoutReversed = false;
    layoutRotation = 0;
    applyLayout();
});

lookupOne("#reverse-layout").addEventListener("click", () => {
    layoutReversed = true;
    layoutRotation = 0;
    applyLayout();
});

lookupOne("#rotate-layout-left").addEventListener("click", () => {
    layoutRotation -= 1;
    applyLayout();
});

lookupOne("#rotate-layout-right").addEventListener("click", () => {
    layoutRotation += 1;
    applyLayout();
});
